import React from 'react'
import { attributes } from '../content/services.md'
import { ServicesPage } from '../content/types'
import { useLocale } from '../hooks/useLocale'
import Head from 'next/head'
import Footer from '../organisms/Footer'
import HeaderBar from '../organisms/HeaderBar'
import ThemeBox from '../atoms/ThemeBox'
import ThemedBackground from '../atoms/ThemedBackground'
import AdaptiveThemeBox from '../atoms/AdaptiveThemeBox'
import MetaInformation from '../utils/MetaInformation'
import LeftCol from '../atoms/LeftCol'
import DoubleHeader from '../molecules/DoubleHeader'
import SpacedRow from '../atoms/SpacedRow'
import RightCol from '../atoms/RightCol'
import Container from '../molecules/Container'
import ServicesNavigation from '../organisms/ServicesNavigation'
import MediumTitle from '@/atoms/MediumTitle'
import Quote from '@/molecules/Quote'
import QuoteAuthor from '@/atoms/QuoteAuthor'
import LargeTitle from '@/atoms/LargeTitle'
import LogoBox from '@/molecules/LogoBox'
import SmallTitle from '@/atoms/SmallTitle'
import SeparatorHeader from '@/atoms/SeparatorHeader'
import UnderlinedLink from '@/atoms/UnderlinedLink'
import FadeWrapper from '@/molecules/FadeWrapper'
import Img from '@/atoms/Img'
import Cases from '@/molecules/Cases'
import ContactCTA from '@/organisms/ContactCTA'
import ImageGrid from '@/atoms/ImageGrid'

export default function Services() {
  const { translations, locale } = useLocale<ServicesPage>(attributes)
  const enableServices = false

  return (
    <>
      <Head>
        <MetaInformation locale={locale} {...translations.meta} />
        <title>{translations.page_title}</title>
      </Head>
      <AdaptiveThemeBox defaultTheme="light">
        <HeaderBar />
      </AdaptiveThemeBox>
      <ThemeBox theme="light">
        <ThemedBackground className="xl:pb-20">
          <Container>
            <SpacedRow>
              <LeftCol below className="lg:-mr-16">
                <DoubleHeader
                  upper={translations.overview.upper_title}
                  main={translations.overview.large_title}
                />
                {enableServices && <ServicesNavigation />}
                <p className="text-sm leading-loose">
                  {translations.overview.intro_text}
                </p>
              </LeftCol>
              <RightCol above className="lg:-ml-12">
                <Img
                  src={translations.overview.intro_image}
                  alt={translations.overview.intro_image_alt}
                />
              </RightCol>
            </SpacedRow>
            <FadeWrapper>
              <SpacedRow>
                <LeftCol className="-mx-4 md:-mx-8 lg:-mr-16">
                  <Img className="w-full" src={translations.software_dev.img} />
                </LeftCol>
                <RightCol>
                  <MediumTitle>{translations.software_dev.title}</MediumTitle>
                  <p className="text-sm leading-loose">
                    {translations.software_dev.content}
                  </p>
                </RightCol>
              </SpacedRow>
              <SpacedRow>
                <LeftCol below className="order-last lg:order-first">
                  <LargeTitle>{translations.data_engineering.title}</LargeTitle>
                  <p className="text-sm leading-loose">
                    {translations.data_engineering.content}
                  </p>
                </LeftCol>
                <RightCol above className="-mx-4 md:-mx-8 lg:-ml-16">
                  <Img
                    className="w-full"
                    src={translations.data_engineering.image}
                  />
                </RightCol>
              </SpacedRow>
              <SpacedRow>
                <LeftCol className="-mx-4 md:-mx-8 lg:-mr-16">
                  <Img
                    className="w-full"
                    src={translations.data_science.left_image}
                  />
                </LeftCol>
                <RightCol>
                  <MediumTitle>
                    {translations.data_science.right_title}
                  </MediumTitle>
                  <p className="text-sm leading-loose">
                    {translations.data_science.right_content}
                  </p>
                </RightCol>
              </SpacedRow>
              <SeparatorHeader
                title={translations.stacks.title}
                extraClasses="py-16"
              />
              <div className="flex justify-center">
                <p className="max-w-4xl leading-loose">
                  {translations.stacks.description}
                </p>
              </div>
              <div className="flex justify-center mt-16 pb-16">
                <ImageGrid images={translations.stacks.stack_cards} />
              </div>
            </FadeWrapper>
          </Container>
        </ThemedBackground>
      </ThemeBox>
      <ThemeBox theme="black">
        <ThemedBackground>
          <Container className="py-8 lg:pt-16">
            <FadeWrapper>
              <div className="my-8 text-center lg:mx-24 lg:mb-24">
                <Quote
                  from={
                    <QuoteAuthor
                      img={translations.black_section.quote.author_image}
                      title={translations.black_section.quote.author_title}
                      from={translations.black_section.quote.author_name}
                    />
                  }
                >
                  {translations.black_section.quote.quote_text}
                </Quote>
              </div>
              <SpacedRow position="items-start">
                <LeftCol className="lg:-mr-8">
                  <Img
                    src={translations.black_section.left_image}
                    className="w-full"
                  />
                  <div className="lg:p-8">
                    <MediumTitle>
                      {translations.black_section.left_title}
                    </MediumTitle>
                    <p className="text-sm leading-loose">
                      {translations.black_section.left_content}
                    </p>
                  </div>
                </LeftCol>
                <RightCol className="lg:-ml-8">
                  <Img
                    src={translations.black_section.right_image}
                    className="w-full"
                  />
                  <div className="lg:p-8">
                    <MediumTitle>
                      {translations.black_section.right_title}
                    </MediumTitle>
                    <p className="text-sm leading-loose">
                      {translations.black_section.right_content}
                    </p>
                  </div>
                </RightCol>
              </SpacedRow>
            </FadeWrapper>
          </Container>
        </ThemedBackground>
      </ThemeBox>
      <ThemeBox theme="bright">
        <ThemedBackground>
          <Container className="px-0">
            <section className="text-black bg-white black:bg-black black:text-white light:bg-gray-700">
              <SeparatorHeader
                title={translations.cases.title}
                subtitle={translations.cases.subtitle}
                extraClasses="py-16"
              />
              <Cases cases={translations.cases.cases} locale={locale} />
              <div className="py-16 text-center">
                <UnderlinedLink
                  className="text-sm"
                  title={translations.cases_readmore}
                  href="/projects"
                />
              </div>
            </section>
          </Container>
        </ThemedBackground>
      </ThemeBox>
      <ThemeBox theme="light">
        <ThemedBackground>
          <FadeWrapper>
            <Container className="pb-20">
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-full text-center max-w-prose">
                  <SmallTitle>{translations.trusted_by.title}</SmallTitle>
                  <LogoBox items={translations.trusted_by.boxes} />
                </div>
              </div>
            </Container>
          </FadeWrapper>
        </ThemedBackground>
      </ThemeBox>
      <ThemeBox theme="bright">
        <ThemedBackground>
          <FadeWrapper>
            <Container>
              <ContactCTA />
            </Container>
          </FadeWrapper>
        </ThemedBackground>
      </ThemeBox>
      <ThemeBox theme="black">
        <Footer />
      </ThemeBox>
    </>
  )
}
